import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Building2, Plus, Edit, Trash2, MapPin, Phone, Mail, 
  Shield, CheckCircle, XCircle, Loader2, Users, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const INSTITUTE_TYPES = [
  'Engineering College',
  'University',
  'International School',
  'Coaching Academy',
  'Medical College',
  'Law School',
  'Management Institute',
  'Polytechnic',
  'Arts & Science College',
  'Vocational Training Center'
];

const COMPLIANCE_BODIES = [
  'AICTE',
  'UGC',
  'CBSE',
  'ICSE',
  'IB',
  'State Board',
  'MCI',
  'BCI',
  'NCTE',
  'None'
];

export default function Institutes() {
  const { user } = useAuth();
  const [institutes, setInstitutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: '',
    compliance_body: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    established_year: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [institutesRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/entities`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/users`, { headers, credentials: 'include' })
      ]);

      if (institutesRes.ok) {
        const data = await institutesRes.json();
        setInstitutes(data);
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      type: '',
      compliance_body: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      established_year: '',
      description: ''
    });
    setIsEditMode(false);
    setSelectedInstitute(null);
  };

  const openAddDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (institute) => {
    setSelectedInstitute(institute);
    setIsEditMode(true);
    setFormData({
      name: institute.name || '',
      code: institute.code || '',
      type: institute.type || '',
      compliance_body: institute.compliance_body || '',
      address: institute.address || '',
      city: institute.city || '',
      state: institute.state || '',
      pincode: institute.pincode || '',
      contact_email: institute.contact_email || '',
      contact_phone: institute.contact_phone || '',
      website: institute.website || '',
      established_year: institute.established_year || '',
      description: institute.description || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code || !formData.type) {
      toast.error('Please fill required fields: Name, Code, and Type');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const url = isEditMode 
        ? `${API_URL}/api/entities/${selectedInstitute.entity_id}`
        : `${API_URL}/api/entities`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(isEditMode ? 'Institute updated successfully' : 'Institute added successfully');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save institute');
      }
    } catch (error) {
      toast.error('Failed to save institute');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (instituteId) => {
    if (!window.confirm('Are you sure you want to delete this institute? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/entities/${instituteId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Institute deleted successfully');
        fetchData();
      } else {
        toast.error('Failed to delete institute');
      }
    } catch (error) {
      toast.error('Failed to delete institute');
    }
  };

  const getHRName = (hrUserId) => {
    const hrUser = users.find(u => u.user_id === hrUserId);
    return hrUser ? hrUser.name || hrUser.email : 'Not Assigned';
  };

  const getEmployeeCount = (entityId) => {
    return users.filter(u => u.entity_id === entityId).length;
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-slate-500">Only Super Admins can manage institutes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="institutes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Institute Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Add and manage institutes under SGI Campus
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={openAddDialog} data-testid="add-institute-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add New Institute
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#DC2626]" />
                {isEditMode ? 'Edit Institute' : 'Add New Institute'}
              </DialogTitle>
              <DialogDescription>
                {isEditMode ? 'Update institute details' : 'Fill in the details to add a new institute to SGI Campus'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 dark:text-white border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Institute Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., SGI Engineering College"
                      data-testid="institute-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Code <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SGIEC"
                      maxLength={10}
                      data-testid="institute-code-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Institute Type <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger data-testid="institute-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INSTITUTE_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Compliance Body</Label>
                    <Select
                      value={formData.compliance_body}
                      onValueChange={(value) => setFormData({ ...formData, compliance_body: value })}
                    >
                      <SelectTrigger data-testid="compliance-body-select">
                        <SelectValue placeholder="Select compliance" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPLIANCE_BODIES.map(body => (
                          <SelectItem key={body} value={body}>{body}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Established Year</Label>
                  <Input
                    type="number"
                    value={formData.established_year}
                    onChange={(e) => setFormData({ ...formData, established_year: e.target.value })}
                    placeholder="e.g., 1990"
                    min="1900"
                    max={new Date().getFullYear()}
                    data-testid="established-year-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 dark:text-white border-b pb-2">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="contact@institute.edu"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+91 1234567890"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.institute.edu"
                    data-testid="website-input"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 dark:text-white border-b pb-2">Address</h3>
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full street address"
                    rows={2}
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State"
                      data-testid="state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PIN Code</Label>
                    <Input
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      placeholder="PIN"
                      maxLength={6}
                      data-testid="pincode-input"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description about the institute..."
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-institute-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {isEditMode ? 'Update Institute' : 'Add Institute'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Institutes</p>
                <p className="text-2xl font-bold">{institutes.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {institutes.filter(i => i.is_active !== false).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">AICTE Compliant</p>
                <p className="text-2xl font-bold text-purple-600">
                  {institutes.filter(i => i.compliance_body === 'AICTE').length}
                </p>
              </div>
              <Shield className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-orange-600">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Institutes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#DC2626]" />
            All Institutes
          </CardTitle>
          <CardDescription>
            Manage institutes under SGI Campus Management System
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
              ))}
            </div>
          ) : institutes.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium">No institutes found</h3>
              <p className="text-slate-500 mb-4">Get started by adding your first institute</p>
              <Button className="btn-primary" onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Institute
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Institute</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Compliance</TableHead>
                  <TableHead>HR Assigned</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {institutes.map((institute) => (
                  <TableRow key={institute.entity_id} data-testid={`institute-row-${institute.entity_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                          {institute.code?.substring(0, 2) || 'IN'}
                        </div>
                        <div>
                          <p className="font-medium">{institute.name}</p>
                          <p className="text-sm text-slate-500">{institute.code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{institute.type || 'Not Set'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        institute.compliance_body === 'AICTE' ? 'bg-purple-100 text-purple-800' :
                        institute.compliance_body === 'UGC' ? 'bg-blue-100 text-blue-800' :
                        'bg-slate-100 text-slate-800'
                      }>
                        {institute.compliance_body || 'None'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getHRName(institute.hr_user_id)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{getEmployeeCount(institute.entity_id)}</span>
                    </TableCell>
                    <TableCell>
                      {institute.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(institute)}
                          data-testid={`edit-institute-${institute.entity_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(institute.entity_id)}
                          data-testid={`delete-institute-${institute.entity_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100">Institute Compliance</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Each institute can have different compliance requirements (AICTE, UGC, CBSE, etc.). 
                The system will enforce appropriate rules for attendance, workload, and API scores 
                based on the selected compliance body.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
